// ============================================================
//  src/pages/HomePage.jsx
// ============================================================
//  No inline styles — all visual rules live in global.css.
//  Font Awesome icons replace all emoji.
// ============================================================

import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ProductCard from "../components/ProductCard";
import WhyChoose from "../components/WhyChoose";
import About from "../components/About";
import { Toast, SectionTitle } from "../components/UI";
import useProducts from "../hooks/useProducts";
import { addItemToCart } from "../utils/helpers";
import { supabase } from "../utils/supabase";               // ← added
import config from "../config";

/* ─────────────────────────────────────────────────────────────
  Inject Font Awesome once — fonts are handled by global.css
───────────────────────────────────────────────────────────── */
const FontLoader = () => {
  useEffect(() => {
    if (document.getElementById("fa-cdn")) return;
    const fa = document.createElement("link");
    fa.id   = "fa-cdn";
    fa.rel  = "stylesheet";
    fa.href = "https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.1/css/all.min.css";
    document.head.appendChild(fa);
  }, []);
  return null;
};

/* ─────────────────────────────────────────────────────────────
  Hero
───────────────────────────────────────────────────────────── */
function Hero() {
  const navigate = useNavigate();

  const slides = [
    {
      image: "/images/millet.jpg",
      title: "Farm Fresh Millets",
      subtitle: "Nutritious, organic millets straight from our farms",
      cta: "Browse Millets",
      ctaIcon: "fa-solid fa-seedling",
      path: "category/millets",
      category: "Millets",
    },
    {
      image: "/images/Traditional-rice.jpg",
      title: "Traditional Rices",
      subtitle: "Rich in antioxidants and great for healthy meals",
      cta: "Explore Rice",
      ctaIcon: "fa-solid fa-bowl-rice",
      path: "category/traditional-rice",
      category: "Rice",
    },
    {
      image: "/images/Nuts.jpg",
      title: "Dry Fruits & Nuts",
      subtitle: "Fully natural, no preservatives, sourced from trusted farmers",
      cta: "Shop Now",
      ctaIcon: "fa-solid fa-store",
      path: "category/dry-fruits-nuts",
      category: "Dry Fruits & Nuts",
    },
    {
      image: "/images/sweetners.jpg",
      title: "Sweeteners",
      subtitle: "Natural sweeteners with low glycemic index, perfect for healthy cooking",
      cta: "Shop Now",
      ctaIcon: "fa-solid fa-jar",
      path: "category/sweeteners",
      category: "Sweeteners",
    },
  ];

  const [active, setActive] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setActive((s) => (s + 1) % slides.length), 4000);
    return () => clearInterval(id);
  }, []);

  const handleCta = () => {
    const { path, category } = slides[active];
    navigate(path, { state: { category } });
    window.scrollTo(0, 0);
  };

  return (
    <section className="hero">
      <div className="hero-inner">

        <div className="hero-left">
          <h1 className="hero-title">{slides[active].title}</h1>
          <p className="hero-sub">{slides[active].subtitle}</p>

          <button className="cta-btn btn-press" onClick={handleCta}>
            <i className={slides[active].ctaIcon} />
            {slides[active].cta}
          </button>

          <div className="hero-dots">
            {slides.map((_, i) => (
              <button
                key={i}
                aria-label={`Go to slide ${i + 1}`}
                className={`dot-btn${i === active ? " active" : ""}`}
                onClick={() => setActive(i)}
              />
            ))}
          </div>
        </div>

        <div className="hero-right">
          <div className="hero-slider">
            {slides.map((s, i) => (
              <div
                key={i}
                className={`slide${i === active ? " active" : ""}`}
                style={{ backgroundImage: `url('${s.image}')` }}
              />
            ))}
          </div>
        </div>

      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
  HomePage
───────────────────────────────────────────────────────────── */
export default function HomePage({ cart, setCart, auth }) {
  const navigate = useNavigate();
  const [category, setCategory] = useState("All");
  const [toast, setToast] = useState(null);
  const [stockMap, setStockMap] = useState({});             // ← added

  const { products, categories, loading } = useProducts();

  // Fetch all products' stock once on mount, then subscribe to realtime updates
  useEffect(() => {
    // 1. Bulk initial fetch
    supabase
      .from('product_stock')
      .select('product_id, status, quantity')
      .then(({ data }) => {
        if (!data) return;
        const map = {};
        data.forEach((row) => { map[String(row.product_id)] = row; });
        setStockMap(map);
      });

    // 2. Realtime — reflect any UPDATE instantly across all cards
    const channel = supabase
      .channel('home-stock')
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'product_stock' },
        (payload) => {
          setStockMap((prev) => ({
            ...prev,
            [String(payload.new.product_id)]: payload.new,
          }));
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, []);

  const filtered =
    category === "All"
      ? products
      : products.filter((p) => p.category === category);

  const handleAddToCart = useCallback(
    (product) => {
      setCart((prev) => addItemToCart(prev, product));
      setToast(`${product.name} added!`);
      navigate("/cart");
      window.scrollTo(0, 0);
    },
    [setCart, navigate]
  );

  function handleBuyNow(product, e) {
    if (e) e.stopPropagation();
    setCart((prev) => addItemToCart(prev, product));
    navigate("/cart");
    window.scrollTo(0, 0);
  }

  function openProduct(product) {
    navigate(`/product/${product.id}`);
    window.scrollTo(0, 0);
  }

  return (
    <>
      <FontLoader />
      <Hero />

      <div id="products-section" className="products-section-wrapper">

        <div className="products-section-header">
          <SectionTitle>Our Products</SectionTitle>
        </div>

        {/* Category pills */}
        <div className="category-pills no-scrollbar">
          {categories.map((c) => (
            <button
              key={c}
              className={`btn-press category-pill${category === c ? " active" : ""}`}
              onClick={() => setCategory(c)}
            >
              {c}
            </button>
          ))}
        </div>

        {/* Skeleton loader */}
        {loading && (
          <div className="product-grid">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="product-skeleton" />
            ))}
          </div>
        )}

        {/* Product grid */}
        {!loading && (
          <div className="product-grid" role="list" aria-label="Products">
            {filtered.map((p) => (
              <div key={p.id} className="product-cell" role="listitem">
                <ProductCard
                  product={p}
                  stockInfo={stockMap[String(p.id)]}        // ← wired up
                  onClick={() => openProduct(p)}
                  onAddToCart={handleAddToCart}
                  onBuyNow={handleBuyNow}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="page-bottom-space" />
      <WhyChoose />
      <About />
      <FeaturesSection />
      <StandardsSection />
      {toast && <Toast message={toast} onDone={() => setToast(null)} />}
    </>
  );
}

/* ─────────────────────────────────────────────────────────────
  Standards & Certifications
───────────────────────────────────────────────────────────── */
function StandardsSection() {
  return (
    <section className="standards-section" aria-label="Standards and Certifications">
      <div className="standards-inner">
        <h2 className="standards-heading">
          <i className="fa-solid fa-award standards-icon" />
          Standards &amp; Certifications
        </h2>
        <p className="standards-desc">
          RK Farm Foods is FSSAI certified — committed to food safety and quality.
        </p>

        <div className="standards-card-wrapper">
          <div className="standards-card">
            <img
              src={`${process.env.PUBLIC_URL || ""}/images/fssai.png`}
              alt="FSSAI certification logo"
              className="fssai-img"
              onError={(e) => { e.target.style.display = "none"; }}
            />
            <div className="fssai-details">
              <p className="fssai-name">FSSAI</p>
              <p className="fssai-full-name">Food Safety &amp; Standards Authority of India</p>
              <p className="fssai-license">
                <strong>License:</strong> {config.fssaiLicense}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
  Features Section
───────────────────────────────────────────────────────────── */
function FeaturesSection() {
  const tiles = [
    { icon: "fa-solid fa-truck-fast",    title: "Quick & Reliable Delivery",  colorClass: "feature-icon--blue"   },
    { icon: "fa-solid fa-headset",       title: "Dedicated Online Support",   colorClass: "feature-icon--red"    },
    { icon: "fa-solid fa-tag",           title: "Factory Price Assurance",    colorClass: "feature-icon--amber"  },
    { icon: "fa-solid fa-shield-halved", title: "Certified Quality & Safety", colorClass: "feature-icon--green"  },
  ];

  return (
    <section className="features-section" aria-label="Why choose RK Farm Foods">
      <div className="features-grid">
        {tiles.map((t) => (
          <div key={t.title} className="feature-tile">
            <i className={`${t.icon} feature-icon ${t.colorClass}`} />
            <p className="feature-title">{t.title}</p>
          </div>
        ))}
      </div>
    </section>
  );
}
